import { Injectable, Injector } from "@angular/core";
import { EventTypePreset } from "@common/ADAPT.Common.Model/organisation/event-type";
import { Item } from "@common/ADAPT.Common.Model/organisation/item";
import { KeyResult } from "@common/ADAPT.Common.Model/organisation/key-result";
import { KeyResultValue } from "@common/ADAPT.Common.Model/organisation/key-result-value";
import { Link } from "@common/ADAPT.Common.Model/organisation/link";
import { Objective } from "@common/ADAPT.Common.Model/organisation/objective";
import { ObjectiveComment } from "@common/ADAPT.Common.Model/organisation/objective-comment";
import { ObjectiveReview } from "@common/ADAPT.Common.Model/organisation/objective-review";
import { ObjectiveStatus } from "@common/ADAPT.Common.Model/organisation/objective-status";
import { ObjectiveType } from "@common/ADAPT.Common.Model/organisation/objective-type";
import { Person } from "@common/ADAPT.Common.Model/person/person";
import { PersonFlag } from "@common/ADAPT.Common.Model/person/person-flag.enum";
import { IBreezeEntity } from "@common/lib/data/breeze-entity.interface";
import { AdaptError } from "@common/lib/error-handler/adapt-error";
import { ObjectUtilities } from "@common/lib/utilities/object-utilities";
import { UserService } from "@common/user/user.service";
import { BaseUiService } from "@common/ux/base-ui.service/base-ui.service";
import { KanbanUiService } from "@org-common/lib/kanban/kanban-ui.service";
import { OrganisationService } from "@org-common/lib/organisation/organisation.service";
import moment from "moment";
import { forkJoin, of, throwError } from "rxjs";
import { catchError, filter, map, switchMap, take, tap } from "rxjs/operators";
import { SelectItemDialogComponent } from "../kanban/items/select-item-dialog/select-item-dialog.component";
import { LinkService } from "../link/link.service";
import { PersonFlagService } from "../person/person-flag.service";
import { ScheduleService } from "../schedule/schedule.service";
import { WorkflowRunDialogComponent } from "../workflow/workflow-run-dialog/workflow-run-dialog.component";
import { AddObjectiveLinkDialogComponent, IAddObjectiveLinkDialogData } from "./add-objective-link-dialog.component/add-objective-link-dialog.component";
import { AddObjectiveWorkflow } from "./add-objective-workflow";
import { DeleteKeyResultConfirmationDialogComponent, IConfirmDeleteKeyResultData } from "./delete-key-result-confirmation-dialog/delete-key-result-confirmation-dialog.component";
import { DeleteObjectiveConfirmationDialogComponent, IConfirmDeleteObjectiveData } from "./delete-objective-confirmation-dialog/delete-objective-confirmation-dialog.component";
import { EditKeyResultDialogComponent } from "./edit-key-result-dialog/edit-key-result-dialog.component";
import { EditObjectiveDialogComponent } from "./edit-objective-dialog/edit-objective-dialog.component";
import { IObjectiveGroup, ObjectivesService } from "./objectives.service";
import { ReorderObjectivesDialogComponent } from "./reorder-objectives-dialog/reorder-objectives-dialog.component";
import { ReviewObjectiveDialogComponent } from "./review-objective-dialog/review-objective-dialog.component";
import { UpdateKeyResultDialogComponent } from "./update-key-result-dialog/update-key-result-dialog.component";

@Injectable({
    providedIn: "root",
})
export class ObjectivesUiService extends BaseUiService {
    public constructor(
        injector: Injector,
        private objectivesService: ObjectivesService,
        private userService: UserService,
        private organisationService: OrganisationService,
        private scheduleService: ScheduleService,
        private personFlagService: PersonFlagService,
        private kanbanUiService: KanbanUiService,
        private linkService: LinkService,
    ) {
        super(injector);
    }


    public reviewObjective(objective: Objective, callback?: (review: ObjectiveReview) => void) {
        return this.objectivesService.getOrCreateReviewForObjective(objective).pipe(
            tap((review) => callback?.(review)),
            switchMap((review) => this.dialogService.open(ReviewObjectiveDialogComponent, review)),
        );
    }

    public dismissObjectiveReview(objective: Objective) {
        return this.dialogService.openConfirmationDialog({
            title: "Dismiss objective review suggestion",
            message: `<p>Are you sure you want to dismiss the reminder to complete a review?</p>
                <p>This suggestion will not be shown again. However, you can still complete the review later from the action menu on this page.</p>`,
            confirmButtonText: "Dismiss",
        }).pipe(
            switchMap(() => this.objectivesService.getOrCreateReviewForObjective(objective)),
            switchMap((review) => {
                review.reviewDismissed = true;
                return this.objectivesService.saveEntities(review);
            }),
        );
    }

    public reorderObjectives(objectiveGroups: IObjectiveGroup[], teamId?: number) {
        return this.dialogService.open(ReorderObjectivesDialogComponent, { objectiveGroups, teamId });
    }

    public createObjectiveForEventTypePreset(preset: EventTypePreset) {
        return this.scheduleService.getLatestMeetingAndSeriesForEventTypePreset(preset).pipe(
            switchMap((meeting) => this.createObjective(undefined, meeting?.meeting?.meetingDateTime)),
        );
    }

    public createObjective(teamId?: number, startDate?: Date, onObjectiveCreated?: (newObjective: Objective) => void) {
        const initialData: Partial<Objective> = {
            creationDate: new Date(),
            dueDate: startDate ?? moment().add(1, "year").toDate(),
            modifiedDate: new Date(),
            status: ObjectiveStatus.OnTrack,
            type: ObjectiveType.Annual,
            teamId,
            description: "", // or froala will chuck an empty string here causing an entity change
            organisationId: this.organisationService.getOrganisationId(),
        };

        return forkJoin([
            this.objectivesService.createObjective(initialData).pipe(
                tap((objective) => onObjectiveCreated?.(objective)),
            ),
            this.personFlagService.isFlagEnabled(PersonFlag.UseLegacyObjectiveCreation),
        ]).pipe(
            switchMap(([objective, useLegacyObjectiveCreation]) => useLegacyObjectiveCreation
                ? this.editObjective(objective)
                : this.openObjectiveCreationWizard(objective)),
        );
    }

    public editObjective(objective: Objective) {
        return this.dialogService.open(EditObjectiveDialogComponent, objective);
    }

    public openObjectiveCreationWizard(objective: Objective) {
        return this.dialogService.open(WorkflowRunDialogComponent, {
            workflow: AddObjectiveWorkflow,
            runData: objective,
        }).pipe(map(() => objective));
    }

    public createKeyResultForObjective(objective: Objective) {
        const initialData: Partial<KeyResult> = {
            objective,
            ordinal: objective.keyResults.length,
            targetValue: 100,
        };
        return this.objectivesService.createKeyResult(initialData).pipe(
            switchMap((keyResult) => this.dialogService.open(EditKeyResultDialogComponent, keyResult)),
        );
    }

    public editKeyResult(keyResult: KeyResult) {
        return this.dialogService.open(EditKeyResultDialogComponent, keyResult);
    }

    public updateKeyResult(keyResult: KeyResult) {
        const currentKeyResultValue = keyResult.currentKeyResultValue;
        return this.userService.currentPerson$.pipe(
            filter(ObjectUtilities.createIsInstanceFilter(Person)),
            take(1),
            switchMap((person) => {
                const objectiveComment$ = this.objectivesService.createObjectiveComment(keyResult.objective);
                const keyResultValue$ = this.objectivesService.createKeyResultValue({
                    keyResult,
                    value: keyResult.currentValue,
                    dateTime: new Date(),
                    person,
                });

                return forkJoin(objectiveComment$, keyResultValue$);
            }),
            switchMap((createdEntities) => this.dialogService.open(UpdateKeyResultDialogComponent, {
                objectiveComment: createdEntities[0],
                keyResultValue: createdEntities[1],
                currentKeyResultValue,
            })),
        );
    }

    public editKeyResultValue(keyResultValue: KeyResultValue, updateComment: ObjectiveComment, previousKeyResultValue?: KeyResultValue) {
        return this.dialogService.open(UpdateKeyResultDialogComponent, {
            objectiveComment: updateComment,
            keyResultValue,
            currentKeyResultValue: previousKeyResultValue,
        });
    }

    public addObjectiveLinkForObjective(objective: Objective) {
        const unsavedLinks: Link[] = [];
        return this.objectivesService.getLinksForObjective(objective).pipe(
            switchMap((links) => {
                const existingLinkedObjectives = links
                    .filter((link) => !!link.objective2) // can be item or agenda item - but here only need objective as it is linking objectives to objective
                    .map((link) => link.objective2!);
                return this.dialogService.open(AddObjectiveLinkDialogComponent, {
                    excludeObjective: objective,
                    disabledObjectives: existingLinkedObjectives,
                } as IAddObjectiveLinkDialogData);
            }),
            switchMap((selectedObjectives) => {
                if (!selectedObjectives || selectedObjectives.length === 0) {
                    return of(undefined);
                }

                return forkJoin(selectedObjectives.map((destObjective) => this.linkService.createObjectiveToObjectiveLink(objective, destObjective))).pipe(
                    tap((links) => unsavedLinks.push(...links)),
                    switchMap((newObjectiveLinks) => this.objectivesService.saveEntities(newObjectiveLinks)),
                );
            }),
            catchError((err: AdaptError) => this.dialogService.showMessageDialog("Error saving link", err.message).pipe(
                switchMap(() => unsavedLinks.length > 0
                    ? this.objectivesService.rejectChanges(unsavedLinks) // just a few selected objectives to link - not even persisted; so just discard them if saving fails
                    : of(undefined)),
                switchMap(() => throwError(() => err)))),
        );
    }

    public addObjectiveItemLinkForObjective(objective: Objective) {
        let newItem: Item | undefined;
        return this.dialogService.open(
            SelectItemDialogComponent,
            { team: objective.team, linking: true, excludedItemIds: objective.links.map((l) => l.itemId ?? -1) },
        ).pipe(
            switchMap((result) => {
                newItem = result.isNew ? result.items[0] : undefined;
                const destItems = result.items;
                if (destItems.length > 0) {
                    return forkJoin(destItems.map((item) => this.objectivesService.createObjectiveItemLink(objective, item)));
                }

                return of([]);
            }),
            switchMap((link) => this.objectivesService.saveEntities(link)),
            switchMap(() => newItem
                ? this.kanbanUiService.promptToAssociateMeetingItem(newItem)
                : of(undefined)),
            catchError((err: AdaptError) => this.dialogService.showMessageDialog("Error saving link", err.message).pipe(
                switchMap(() => throwError(() => err)),
            )),
        );
    }

    public promptToDeleteObjectiveLink(link: Link) {
        return this.promptToDeleteAndSaveEntities(
            [link],
            "Delete objective link",
            `<p>The objective link between <i>${link.objective1!.title}</i> and <i>${link.objective2!.title}</i> will be deleted.</p>
                <p>Are you sure you want to delete?</p>`,
        );
    }

    public promptToDeleteObjectiveItemLink(link: Link) {
        return this.promptToDeleteAndSaveEntities(
            [link],
            "Delete objective action link",
            `<p>The link between objective <i>${link.objective1!.title}</i> and action <i>${link.item!.code}</i> will be deleted.</p>
                <p>Are you sure you want to delete?</p>`,
        );
    }

    public promptToDeleteKeyResult(keyResult: KeyResult) {
        // don't even have to delete the KR values and it will cascade delete
        const dialogData: IConfirmDeleteKeyResultData = { keyResult };
        const confirmationDialog$ = this.dialogService.open(
            DeleteKeyResultConfirmationDialogComponent,
            dialogData,
        ).pipe(
            map((dialogResponse) => dialogResponse!.result),
        );

        // identify all update comments for the KR
        const updateComments = keyResult.objective.comments.filter((comment) =>
            keyResult.values.some((value) => value.dateTime.getTime() === comment.dateTime.getTime()));

        return this.deleteAndSaveEntities(
            confirmationDialog$,
            [keyResult, ...updateComments],
            "Error Deleting Key Result",
        );
    }

    public promptToDeleteObjective(objective: Objective) {
        const dialogData: IConfirmDeleteObjectiveData = { objective };

        const childObjectives: Objective[] = [];
        const confirmationDialog$ = this.dialogService.open(
            DeleteObjectiveConfirmationDialogComponent,
            dialogData,
        ).pipe(
            map((dialogResponse) => dialogResponse!.result),
            filter((acceptDelete) => !!acceptDelete),
            // prime children
            switchMap(() => this.objectivesService.getObjectivesWithParentId(objective.objectiveId)),
            switchMap(() => {
                // unlink the children first, otherwise we get SAME TABLE REFERENCE constraint error from DB
                // it seems the objective delete SQL is done before the childObjective update by EF
                for (const childObjective of objective.childObjectives) {
                    childObjective.parentObjectiveId = undefined;
                    childObjectives.push(childObjective);
                }

                return this.commonDataService.saveEntities(childObjectives);
            }),
            catchError((err: AdaptError) => {
                for (const entity of childObjectives) {
                    entity.entityAspect.rejectChanges();
                }

                return this.dialogService.showErrorDialog(
                    "Error unlinking child objectives",
                    `<p>Message: ${err.message}<p>
                     <p>Please try again.</p>`,
                );
            }),
            map(() => true),
        );

        return this.deleteAndSaveEntities(
            confirmationDialog$,
            [objective],
            "Error Deleting Objective",
        );
    }

    public promptToDeleteKeyResultValue(keyResultValue: KeyResultValue, updateComment?: ObjectiveComment) {
        const deleteEntities: IBreezeEntity[] = [keyResultValue];
        if (updateComment) {
            deleteEntities.push(updateComment);
        }

        return this.promptToDeleteAndSaveEntities(
            deleteEntities,
            "Delete Key Result Value?",
            `<p>The key result value together with its comment will be deleted and cannot be restored.</p>
                <p>Are you sure you want to delete?</p>`);
    }
}
